import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { fetchAuthSession } from "aws-amplify/auth";
import { remove, uploadData } from "aws-amplify/storage";
import localforage from "localforage";
import { client } from "../App";
import Configuration from "../config";
import { LoadingErrorType, UserRoleEnum } from "../helpers/enums";
import {
  cognitoUserToObject,
  getCookie,
  GetCustomerTransformToProfile,
  GetOperatorTransformToProfile,
  handlerErrors,
} from "../helpers/helpers";
import { getEvictionForMutation, UPDATE_PROFILE_IMAGE } from "../helpers/mutations";
import { GET_OPERATOR, GET_USER } from "../helpers/queries";
import { Bill } from "../models/Bill";
import { Contract } from "../models/Contract";
import { UsageData } from "../models/UsageData";
import { ProfileImage, User, UserProfile } from "../models/User";
import { AppDispatch } from "../store";
import { hideLoading, showLoading } from "./app";
import { loadContracts } from "./contracts";
import { resetContractSelected } from "./usageData";

const AWS = require("aws-sdk");

export interface SearchUser {
  username: string | null;
  email: string | null;
  name: string | "";
  role: string | null;
  surname: string | "";
  safoAccountId: string | null;
  emailVerified: boolean | true;
  status: string | null;
  fiscalCode: string | null;
  impersonateToken: string | null;
  isCondominiumAdminDisabled: boolean | false;
  authIssuer: string | null;
}

export interface ReloadSearchUser {
  email: string | null;
  status: boolean | false;
}

export interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  profileImpersonate: UserProfile | null;
  impersonationActive: boolean;
  impersonationToken: string | null;
  contracts: Array<Contract>;
  bills: Array<Bill>;
  usageData: Array<UsageData>;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  userFetched: boolean;
  searchUser: SearchUser | null;
  reloadSearchUser: ReloadSearchUser;
  auth_issuer: string | null;
  appAuthorizationToken: string | null;
  skipSpid: boolean | null;
}

const initialState: AuthState = {
  user: null,
  profile: null,
  profileImpersonate: null,
  impersonationToken: null,
  impersonationActive: false,
  contracts: [],
  bills: [],
  usageData: [],
  isLoading: false,
  isError: false,
  error: null,
  userFetched: false,
  searchUser: null,
  reloadSearchUser: { email: null, status: false },
  auth_issuer: null,
  appAuthorizationToken: null,
  skipSpid: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    fetchInit(state, action: PayloadAction<any | null>): void {
      state.isLoading = true;
      state.isError = false;
    },
    fetchSuccess(state, action: PayloadAction<any | null>): void {
      state.isLoading = false;
      state.isError = false;
      state.user = action.payload;
    },
    fetchFailure(state, action: PayloadAction<any | null>): void {
      state.isLoading = true;
      state.isError = false;
    },
    userFetched(state, action: PayloadAction<boolean>): void {
      state.userFetched = true;
    },
    resetData(state, action: PayloadAction<any | null>): void {
      state.user = null;
      state.profile = null;
      state.contracts = [];
    },
    receivedUserProfile(state, action: PayloadAction<any | null>): void {
      state.profile = action.payload;
    },
    receivedLoginMethod(state, action: PayloadAction<any | null>): void {
      state.auth_issuer = action.payload;
    },
    receivedContracts(state, action: PayloadAction<any | null>): void {
      state.contracts = action.payload;
    },
    receivedBills(state, action: PayloadAction<any | null>): void {
      state.bills = action.payload;
    },
    receivedUsageData(state, action: PayloadAction<any | null>): void {
      state.usageData = action.payload;
    },
    setLoadingError(state, action: PayloadAction<any | null>): void {
      state.error = action.payload;
    },
    receivedUserProfileImpersonate(
      state,
      action: PayloadAction<any | null>
    ): void {
      state.profileImpersonate = action.payload;
    },
    setImpersonationToken(state, action: PayloadAction<any | null>): void {
      state.impersonationToken = action.payload;
    },
    setSearchUser(state, action: PayloadAction<any | null>): void {
      state.searchUser = action.payload;
    },
    setReloadSearchUser(state, action: PayloadAction<any | null>): void {
      state.reloadSearchUser = action.payload;
    },
    setActiveImpersonation(state, action: PayloadAction<any | null>): void {
      state.impersonationActive = action.payload;
    },
    unsetImpersonateData(state): void {
      state.impersonationToken = null;
      state.profileImpersonate = null;
      state.impersonationActive = false;
      state.contracts = [];
      state.searchUser = null;
    },
    setPersistState(state, action: PayloadAction<any | null>): void {
      state.impersonationActive = action.payload.impersonationActive;
      state.impersonationToken = action.payload.impersonationToken;
      state.profileImpersonate = action.payload.profileImpersonate;
    },
    setAppAuthorizationToken(state, action: PayloadAction<any | null>): void {
      state.appAuthorizationToken = action.payload;
    },
    setIssuers(state, action: PayloadAction<any | null>): void {
      state.auth_issuer = action.payload;
    },
    setSkipSpid(state, action: PayloadAction<any | null>): void {
      state.skipSpid = action.payload;
    },
  },
});

export const {
  fetchInit,
  fetchSuccess,
  fetchFailure,
  resetData,
  receivedUserProfile,
  receivedLoginMethod,
  receivedUserProfileImpersonate,
  receivedContracts,
  receivedBills,
  receivedUsageData,
  setLoadingError,
  setImpersonationToken,
  setSearchUser,
  setReloadSearchUser,
  setActiveImpersonation,
  unsetImpersonateData,
  userFetched,
  setPersistState,
  setAppAuthorizationToken,
  setIssuers,
  setSkipSpid,
} = authSlice.actions;

export default authSlice.reducer;
export const manageReloadSearchUser =
  (data: ReloadSearchUser) => (dispatch: Dispatch) => {
    dispatch(setReloadSearchUser(data));
  };
export const persistAuth = (persist: AuthState) => (dispatch: Dispatch) => {
  dispatch(setPersistState(persist));
};
export const unsetImpersonation = () => (dispatch: Dispatch) => {
  dispatch(unsetImpersonateData());
};
export const setWebViewToken = (token: string) => (dispatch: AppDispatch) => {
  dispatch(setAppAuthorizationToken(token));
};

export const setImpersonation =
  (active: boolean) => (dispatch: Dispatch, getState: any) => {
    dispatch(setActiveImpersonation(active));
  };

export const setPersistAuth =
  () => async (dispatch: Dispatch, getState: any) => {
    await localforage
      .setItem("state", {
        impersonation: {
          impersonationActive: getState().auth.impersonationActive,
          impersonationToken: getState().auth.impersonationToken,
          profileImpersonate: getState().auth.profileImpersonate,
        },
      })
      .then()
      .catch();
  };

export const removePersistence = () => async () => {
  await localforage.removeItem("state");
  await localforage.removeItem("contract");
};

export const setImpersonateToken = (token: string) => (dispatch: Dispatch) => {
  dispatch(setImpersonationToken("Bearer " + token));
};
export const setSearchUserResult =
  (searchUser: SearchUser) => (dispatch: Dispatch) => {
    dispatch(setSearchUser(searchUser));
  };
export const getUserProfile = () => (dispatch: Dispatch, getState: any) => {
  const auth = getState().auth;
  const { impersonationToken } = auth;
  if (impersonationToken) {
    dispatch<any>(getUserProfileImpersonate());
  }
  return client()
    .query({
      query: GET_USER,
      variables: {},
    })
    .then(({ data }: { data: any }) => {
      return dispatch(
        receivedUserProfile(GetCustomerTransformToProfile(data.getCustomer))
      );
    })
    .catch((e: any) => {
      const errors = handlerErrors(
        e,
        () => {},
        () => {}
      );
      if ("error_network" in errors) {
        return dispatch(setLoadingError(errors["error_network"]));
      }
      console.log("[APP ERROR:]", e);
    });
};

export const getUserProfileImpersonate =
  () => (dispatch: Dispatch, getState: any) => {
    return client()
      .query({
        query: GET_USER,
        variables: {},
        fetchPolicy: "network-only",
      })
      .then(({ data }: { data: any }) => {
        dispatch(
          receivedUserProfileImpersonate(
            GetCustomerTransformToProfile(data.getCustomer)
          )
        );
        return dispatch<any>(loadContracts());
      })
      .then(() => dispatch<any>(setPersistAuth()))
      .catch((e: any) => {
        const errors = handlerErrors(
          e,
          () => {},
          () => {}
        );
        if ("error_network" in errors) {
          return dispatch(setLoadingError(errors["error_network"]));
        }
        console.log("[APP ERROR:]", e);
      });
  };

export const uploadImageToS3 =
  (fileNameUpload: string, fileUploaded: any, client: any, setPopup: any) =>
  async (dispatch: Dispatch) => {
    dispatch<any>(showLoading());

    try {
      const uploadResult = await uploadData({
        key: fileNameUpload,
        data: fileUploaded,
        options: {
          accessLevel: "protected",
          contentType: fileUploaded.type,
        },
      }).result;

      const data = ProfileImage.makeData(uploadResult.key);

      await client().mutate({
        mutation: UPDATE_PROFILE_IMAGE,
        variables: { fileName: data },
        update: (cache: any) => {
        const fieldsToEvict = getEvictionForMutation("updateProfilePicture");
        fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
        cache.gc();
      },
      });

      dispatch<any>(fetchUser());
      dispatch<any>(hideLoading());

      if (setPopup) {
        setPopup(false);
      }
    } catch (err) {
      console.log("ERR Upload/Mutation", err);
      dispatch<any>(hideLoading());
      dispatch(setLoadingError(LoadingErrorType.CannotUploadImage));
    }
  };

export const deleteImage = (client: any) => (dispatch: Dispatch) => {
  dispatch<any>(showLoading());
  const data = ProfileImage.makeData("");

  client()
    .mutate({
      mutation: UPDATE_PROFILE_IMAGE,
      variables: { fileName: data },
      update: (cache: any) => {
        const fieldsToEvict = getEvictionForMutation("updateProfilePicture");
        fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
        cache.gc();
      },
    })
    .then((res: any) => {
      dispatch<any>(fetchUser());
      dispatch<any>(hideLoading());
    })
    .catch((e: any) => {
      dispatch(setLoadingError(LoadingErrorType.CannotDeleteImage));
      dispatch<any>(hideLoading());
    });
};
export const wipeUser = () => (dispatch: Dispatch, getState: any) => {
  dispatch<any>(resetData(getState()));
  dispatch<any>(resetContractSelected());
  dispatch<any>(removePersistence());
  dispatch<any>(unsetImpersonation());
};

export const getAmplifyUser = async () => {
  try {
    const session = await fetchAuthSession();

    const idTokenPayload = session.tokens?.idToken?.payload;

    if (!idTokenPayload) {
      throw new Error("No ID Token found in session");
    }

    return cognitoUserToObject(idTokenPayload);
  } catch (error) {
    console.error("Error getting Amplify user:", error);
    throw error;
  }
};
const getGQLUserBO = () => {
  return new Promise((resolve, reject) => {
    return client()
      .query({
        query: GET_OPERATOR,
        variables: {},
        fetchPolicy: "network-only",
      })
      .then((response: any) => {
        return GetOperatorTransformToProfile(response.data.getOperator);
      })
      .then((response: any) => resolve(response))
      .catch((error: any) => reject(error));
  });
};
const getGQLUser = () => {
  return new Promise((resolve, reject) => {
    return client()
      .query({
        query: GET_USER,
        variables: {},
      })
      .then((response: any) =>
        GetCustomerTransformToProfile(response.data.getCustomer)
      )
      .then((response: any) => resolve(response))
      .catch((error: any) => reject(error));
  });
};

export function fetchUser(where: any = null) {
  return async function (dispatch: AppDispatch, getState: any) {
    try {
      const token = getCookie("x-app-webview-authorization");

      if (token && token !== "") {
        const jwt = require("jsonwebtoken");

        const decoded = jwt.decode(token);
        const { email, role } = decoded;
        const username = decoded["cognito:username"];
        dispatch(setWebViewToken(token));

        let user = {
          username,
          role,
          email,
        };

        dispatch(showLoading());

        const profile =
          user.role === UserRoleEnum.MUNICIPAL_OPERATOR ||
          user.role === UserRoleEnum.OPERATOR ||
          user.role === UserRoleEnum.MANAGER
            ? await getGQLUserBO()
            : await getGQLUser();

        dispatch(userFetched(true));
        dispatch(receivedUserProfile(profile));

        const state = getState();
        const { current } = state.contracts;
        const contracts =
          (user.role === UserRoleEnum.CONDOMINIUM_ADMIN ||
            user.role === UserRoleEnum.PRIVATE ||
            user.role === UserRoleEnum.COMPANY ||
            user.role === UserRoleEnum.MUNICIPAL_USER) &&
          !current
            ? await dispatch(loadContracts())
            : await [];

        dispatch(hideLoading());

        return { user, profile, contracts };
      } else {
        let user: any;
        dispatch(showLoading());

        const session = await fetchAuthSession();

        const idTokenPayload = session.tokens?.idToken?.payload;

        if (!idTokenPayload) {
          throw new Error("No valid session found");
        }

        user = cognitoUserToObject(idTokenPayload);
        console.log("USER ROLE: ", user.role);
        const profile: any =
          user.role === UserRoleEnum.MUNICIPAL_OPERATOR ||
          user.role === UserRoleEnum.OPERATOR ||
          user.role === UserRoleEnum.MANAGER
            ? await getGQLUserBO()
            : await getGQLUser();

        dispatch(fetchSuccess(user));
        dispatch(receivedUserProfile(profile));

        const authIssuer = idTokenPayload["auth_issuer"];
        dispatch(receivedLoginMethod(authIssuer));

        const expirationDate = new Date().getTime() + 86400000; // 1 day
        const userDetailsForAEM = {
          name: profile.name,
          surname: profile.surname,
          email: profile.email,
          expirationDate,
        };
        localStorage.setItem(
          "privateUserDetails",
          JSON.stringify(userDetailsForAEM)
        );

        const state = getState();
        const { impersonationActive } = getState().auth;
        const { current } = state.contracts;

        const contracts =
          (user.role === UserRoleEnum.CONDOMINIUM_ADMIN ||
            user.role === UserRoleEnum.PRIVATE ||
            user.role === UserRoleEnum.COMPANY ||
            user.role === UserRoleEnum.MUNICIPAL_USER) &&
          !current
            ? await dispatch(loadContracts())
            : await [];

        if (impersonationActive) {
          await dispatch(loadContracts());
        }

        dispatch(hideLoading());
        dispatch(userFetched(true));

        return { user, profile, contracts };
      }
    } catch (e) {
      dispatch(hideLoading());

      dispatch(userFetched(true));
    }
  };
}

export const uploadFileToS3 = async (
  fileNameUpload: string,
  fileUploaded: File
) => {
  const presignedResponse = await fetch(
    Configuration.get("REACT_APP_API_LOG") + "/uploadS3",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": Configuration.get("REACT_APP_API_KEY"),
      },
      body: JSON.stringify({
        fileName: fileNameUpload,
      }),
    }
  );
  if (!presignedResponse.ok) {
    throw new Error("Presigned_POST_failed");
  }

  const { uploadUrl } = await presignedResponse.json();

  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": fileUploaded.type,
    },
    body: fileUploaded,
  });

  if (!uploadResponse.ok) {
    throw new Error("Upload_S3_failed");
  }

  return true;
};
export const deleteFile = async (fileName: string) => {
  try {
    const result = await remove({
      key: fileName,
      options: {
        accessLevel: "guest",
      },
    });

    return result;
  } catch (err) {
    console.log("Error deleting file:", err);
  }
};
export const setSkipSpidAction = (spid: boolean) => (dispatch: Dispatch) => {
  dispatch(setSkipSpid(spid));
};
